import { makeAutoObservable, runInAction } from 'mobx'
import { PaymentPlanKey, PaymentPlan } from 'api/clients/PaymentPlanClient'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { Stripe, StripeElements } from '@stripe/stripe-js'

export enum PaywallScreen {
  PLAN_SELECT = 'planSelect',
  ALL_PLANS = 'allPlans',
  CHECKOUT = 'checkout',
  SUCCESS = 'success',
}

export enum BillingFrequency {
  ANNUAL = 'annual',
  MONTHLY = 'monthly',
}

export enum PaymentStatus {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

export class PaywallStore {
  // Current screen in the paywall flow
  currentScreen = PaywallScreen.PLAN_SELECT
  previousScreen: PaywallScreen | null = null

  // Selection state
  selectedBillingFrequency = BillingFrequency.ANNUAL
  selectedPlanKey = PaymentPlanKey.BlazePro

  // Checkout state
  paymentStatus = PaymentStatus.IDLE
  errorMessage: string | null = null

  // Loading state
  isInitializing = false

  constructor(private rootStore: any) {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  reset() {
    this.currentScreen = PaywallScreen.PLAN_SELECT
    this.selectedBillingFrequency = BillingFrequency.ANNUAL
    this.selectedPlanKey = PaymentPlanKey.BlazePro
    this.paymentStatus = PaymentStatus.IDLE
    this.errorMessage = null
  }

  // INITIALIZATION
  async ensureInitialized(workspaceId: number) {
    const { tierUsageStore } = this.rootStore

    if (!tierUsageStore.initialized && !tierUsageStore.loading) {
      this.isInitializing = true
      await tierUsageStore.fetch(workspaceId)
      runInAction(() => {
        this.isInitializing = false
      })
    }
  }

  // NAVIGATION
  goToPlanSelect() {
    this.previousScreen = this.currentScreen
    this.currentScreen = PaywallScreen.PLAN_SELECT
  }

  goToAllPlans() {
    this.previousScreen = this.currentScreen
    this.currentScreen = PaywallScreen.ALL_PLANS
  }

  goToCheckout() {
    this.previousScreen = this.currentScreen
    this.currentScreen = PaywallScreen.CHECKOUT
  }

  goToSuccess() {
    this.currentScreen = PaywallScreen.SUCCESS
  }

  goToPreviousScreen() {
    if (this.previousScreen) {
      this.currentScreen = this.previousScreen
      this.previousScreen = null
    }
  }

  // SELECTION
  selectBillingFrequency(frequency: BillingFrequency) {
    this.selectedBillingFrequency = frequency
  }

  selectPlan(planKey: PaymentPlanKey) {
    this.selectedPlanKey = planKey
  }

  // PLAN DATA
  getSelectedPlan(): PaymentPlan | undefined {
    const { tierUsageStore } = this.rootStore
    return tierUsageStore.paymentPlans.find((plan: PaymentPlan) => plan.key === this.selectedPlanKey)
  }

  getMonthlyPrice(discount = 0.0): number {
    const plan = this.getSelectedPlan()
    if (!plan) return 0

    const price =
      this.selectedBillingFrequency === BillingFrequency.ANNUAL
        ? Math.round((plan.price.year.unit_amount / 12) * (1 - discount))
        : plan.price.month.unit_amount * (1 - discount)

    return price
  }

  getPriceId(): string {
    const plan = this.getSelectedPlan()
    if (!plan) throw new Error('Selected plan not found')

    return this.selectedBillingFrequency === BillingFrequency.MONTHLY
      ? plan.price.month.price_id
      : plan.price.year.price_id
  }

  // ERROR HANDLING
  setError(message: string | null) {
    this.errorMessage = message
  }

  // CHECKOUT PROCESSING
  async processCheckout(
  stripe: Stripe | null,
  elements: StripeElements | null,
  cardHolderName: string,
  workspaceId: number,
): Promise<boolean> {
  if (!stripe || !elements || !workspaceId) {
    this.setError('Payment processing unavailable')
    return false
  }

  this.paymentStatus = PaymentStatus.PROCESSING
  this.errorMessage  = null

  
  const { setupIntent, error: setupError } = await stripe.confirmSetup({
    elements,
    confirmParams: {
      // eslint-disable-next-line no-restricted-properties
      return_url: window.location.href, 
      payment_method_data: {
        billing_details: { name: cardHolderName || '' },
      },
    },
    redirect: 'if_required',
  })

  if (setupError || !setupIntent?.payment_method) {
    this.setError(setupError?.message || 'Could not save card')
    return false
  }

  const paymentMethodId = setupIntent.payment_method as string

  const priceId   = this.getPriceId()
  const frequency =
    this.selectedBillingFrequency === BillingFrequency.MONTHLY ? 'month' : 'year'

  const response = await WorkspaceClient.createSubscription(
    workspaceId,
    paymentMethodId,
    this.selectedPlanKey,
    0,
    frequency,
    priceId,
  )

  if (!response.data.success) {
    this.setError(response.data.message || 'Could not create subscription')
    return false
  }

  this.paymentStatus = PaymentStatus.SUCCESS
  return true
}


  // COMPUTED
  get isProcessing() {
    return this.paymentStatus === PaymentStatus.PROCESSING
  }

  get isLoading() {
    const { tierUsageStore } = this.rootStore
    return this.isInitializing || tierUsageStore.loading || !tierUsageStore.initialized
  }

  get isFullHeight() {
    return this.currentScreen !== PaywallScreen.PLAN_SELECT
  }
}
